import { scannerMessages } from "constants/scanner.messages";
import { DriverLifecycleHooks } from "lib/deviceDrivers/driverLifecycle";

import { DriverActionResult } from "./adapters/driverEffects";
import { Xrs2iDriverSession } from "./runtime/Xrs2iDriverSession";
import {
  createSessionStorage,
  DeviceLike,
  getDeviceId,
} from "lib/deviceDrivers/sessionFramework/runtime/DriverSession";

/**
 * Public entrypoint for the TruTest XRS2i driver.
 * It exposes the driver name, supported scanner messages, and the session map that keeps
 * one protocol session alive per device ID so repeated commands share the same state.
 */
export const TT_XRS2I_DRIVER_NAME = "tt-xrs2i";
export const TT_XRS2I_CAPABILITIES = [
  scannerMessages.CANCEL_READ_SAVED_SCANS,
  scannerMessages.READ_SAVED_SCANS,
];

const sessions = createSessionStorage(Xrs2iDriverSession);

/**
 * Starts the saved-scan download flow for a device.
 * The session object keeps command sequencing, retries, and progress reporting isolated from callers.
 */
export function truTestXRS2IReadSavedScans(
  deviceId: string,
): DriverActionResult {
  return sessions.getOrCreateSession(deviceId).readSavedScans();
}

/**
 * Synchronises the scanner hardware state before normal interaction resumes.
 * This is used when the driver needs the device back in a known protocol mode.
 */
export function truTestXRS2ISyncState(deviceId: string): DriverActionResult {
  return sessions.getOrCreateSession(deviceId).syncState();
}

/**
 * Cancels an active saved-scan download for the given device.
 * The session is left alive long enough to ignore late frames from the cancelled request.
 */
export function truTestXRS2ICancelReadSavedScans(
  deviceId: string,
): DriverActionResult {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }

  return session.cancelReadSavedScans();
}

/**
 * Destroys the driver session for a device and removes its cached protocol state.
 * This prevents stale retries or queued commands from leaking into a future connection.
 */
export function truTestXRS2IDestroySession(deviceId: string): void {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }

  session.destroy();
  sessions.dispose(deviceId);
}

/**
 * Flushes any pending timer-driven work for the device session.
 * The caller uses this when delayed retries or completion work need to be turned into Redux actions.
 */
function truTestXRS2ITick(deviceId: string): DriverActionResult {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }

  return session.tick();
}

/**
 * Routes raw scanner payloads into the device session for the matching device ID.
 * This keeps frame parsing, download state, and scan conversion inside the protocol layer.
 */
export const truTestXRS2IProcessMessage = (
  device: DeviceLike,
  _type: string,
  _status: unknown,
  serializedPayload: string,
): DriverActionResult => {
  const deviceId = getDeviceId(device);
  if (!deviceId) {
    return;
  }

  return sessions.getOrCreateSession(deviceId).receive(serializedPayload);
};

/**
 * Handles scanner messages that are triggered by the Redux layer.
 * The handler keeps the UI-facing action names separate from the lower-level protocol work.
 */
export const TT_XRS2I_HANDLER = {
  [scannerMessages.CANCEL_READ_SAVED_SCANS]: ({
    deviceId,
  }: {
    deviceId: string;
  }) => truTestXRS2ICancelReadSavedScans(deviceId),
  [scannerMessages.READ_SAVED_SCANS]: ({ deviceId }: { deviceId: string }) =>
    truTestXRS2IReadSavedScans(deviceId),
};

/**
 * Driver-owned lifecycle hooks consumed by the generic device saga.
 */
export const TT_XRS2I_DRIVER_LIFECYCLE: DriverLifecycleHooks<DriverActionResult> =
  {
    onConnected: truTestXRS2ISyncState,
    onDisconnected: truTestXRS2IDestroySession,
    onTick: truTestXRS2ITick,
    tickIntervalMs: 1000,
  };
